import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import Loader from 'deprecated-shared-ui/templates/loader/default';
import AlpiInformation from './AlpiInformation.jsx';
import ColorSwatchGrid from './ColorSwatchGrid';
import HeroPictureSlider from './HeroPictureSlider.jsx';
import {Constants} from '../view-constants';
import WindowManager from '@core/scripts/components/window-manager';
import {articleOverviewStaticContext} from '../ArticleOverviewStatic.jsx';
import Benefits from './Benefits.jsx';
import {DefaultFallback} from '@shopping-funnel/scripts/components/fallbacks/DefaultFallback.jsx';
import TrinityHelper from '@product/helper/trinity/trinity.js';
import WishlistHeart from '@shopping-funnel/scripts/components/wishlist/WishlistHeart.jsx';
import {buildAlpiJshKey} from '@article-overview/utils/build-alpi-jsh-key.js';
import {useSharedIntersectionObserver} from '../hooks/use-shared-intersection-observer.js';
import Toggles from "@core/scripts/modules/toggles.js";
import {useArticleNavigation} from '@article-overview/hooks/useArticleNavigation.ts';
import {buildSearchAttribution} from '@article-overview/utils/build-search-attribution';
import ModelContainer from '@core/scripts/components/model-container';
import {sendViewedItemsEvent} from '@shopping-funnel/scripts/helper/algolia-tracking';
import {encodeFilterContext, getAlgoliaFilterStringsFromFilterTokens} from '@shopping-funnel/scripts/helper/algolia-filter-context.ts';
import {sendProductClick, toLong} from '@article-overview/components/tracking/product-click-events';


 function Article({
    givenAlpi,
    shopContent,
    positionIndex,
    positionOnPage,
    positionInList,
    filterTrackingValue,
    rankingTrackingValue,
    pointOfSaleId,
    filterTokens
}) {
    const {
        wishlistEnabled,
        imagePresentationTypeId,
        responseMetadata,
        loadMoreButtonTestGroup,
        pageType,
    } = useContext(articleOverviewStaticContext);

    const searchIndexName = ModelContainer.get('searchIndexName');
    const activeSearchEngine = ModelContainer.get('activeSearchEngine');

    const searchAttribution = useMemo(
        () =>
            buildSearchAttribution(
                responseMetadata,
                searchIndexName,
                activeSearchEngine,
                positionIndex,
                loadMoreButtonTestGroup,
                givenAlpi.gridItem?.algoliaObjectId ?? undefined,
            ),
        [
            responseMetadata,
            searchIndexName,
            activeSearchEngine,
            positionIndex,
            loadMoreButtonTestGroup,
            givenAlpi.gridItem?.algoliaObjectId,
        ],
    );
    const algoliaFilters = useMemo(() => getAlgoliaFilterStringsFromFilterTokens(filterTokens), [filterTokens]);
    const [activeIndex, setActiveIndex] = useState(0);
    const [isImageLoaded, setIsImageLoaded] = useState(true);
    const [isVisible, setIsVisible] = useState(positionIndex <= 10);
    const articleContainer = useRef(null);
    const hasBeenVisible = useRef(false);
    const hasSentViewedEvent = useRef(false);
    const firstViewColors = calculateFirstViewColors(givenAlpi.gridItem);
    const currentColor = firstViewColors[activeIndex];
    const preselection = givenAlpi.gridItem.features?.includes(Constants.ALPI_FEATURE_PRESELECTION);
    const {navigateToProduct} = useArticleNavigation();

    // Filter size filters from filter tokens
    const sizeFilters = filterTokens?.filter(token => token.filterIdentifier === 'groesse') || [];
    const sizeFilterValues = sizeFilters.map(filter => filter.filterOptionIdentifier.toLowerCase());

    let itemOptionId = null;
    if (preselection) {
        if (sizeFilterValues.length > 1) {
            // Try to find a matching size in currentColor.sizes
            const matchingSize = currentColor.sizes.find(size => {
                const sizeName = size.name.toLowerCase();

                // Extract size values: split by / and extract value from braces if present
                const sizeValues = [];

                // Check for braces and extract the value inside
                const bracesMatch = sizeName.match(/\(([^)]+)\)/);
                if (bracesMatch) {
                    sizeValues.push(bracesMatch[1].trim());
                }

                // Split by / to get individual sizes
                const splitSizes = sizeName.split('/').map(s => {
                    // Remove braces part and trim
                    return s.replace(/\([^)]*\)/, '').trim();
                });
                sizeValues.push(...splitSizes);

                // Check if all filter values are present in the size values
                return sizeFilterValues.every(filterValue =>
                    sizeValues.some(sizeValue => sizeValue === filterValue)
                );
            });

            // Only preselect a real item option; currentColor.id is an item id, not an item option id
            itemOptionId = matchingSize ? matchingSize.id : null;
        } else if(sizeFilterValues.length === 1) {
            // If we have only one filter, the first size is the matching size
            itemOptionId = currentColor.sizes[0].id;
        } else {
            // No active size filter, so there is no size to preselect
            itemOptionId = null;
        }
    }


    const SHOW_MULTI_PACK = Toggles.isEnabled('SHOW_MULTI_PACK');
    const title = givenAlpi?.gridItem?.title;
    const pack = title
         ?.match(/\(?(\d+er[- ]?Pack)\)?/i)?.[1]
         ?.replace('-', ' ');

    const updatedBenefits = [
         ...(currentColor.benefits || []),
         ...(SHOW_MULTI_PACK && pack
             ? [{ name: pack, displayType: "PRODUCT" }]
             : [])
    ];

    const handleIntersection = useCallback((entries) => {
        entries.forEach((entry) => {
            if (entry.isIntersecting && !hasBeenVisible.current) {
                hasBeenVisible.current = true;
                setIsVisible(true);

                const firstAvailableItemOptionId = firstViewColors
                    .flatMap((color) => color.sizes || [])
                    .find((size) => size?.id)?.id;

                if (!searchAttribution && firstAvailableItemOptionId && !hasSentViewedEvent.current) {
                    hasSentViewedEvent.current = true;
                    sendViewedItemsEvent([String(firstAvailableItemOptionId)]);
                }
            }
        });
    }, [firstViewColors, searchAttribution]);

    useSharedIntersectionObserver(articleContainer, handleIntersection);

    function onChangeItem(color, isHover) {
        const newIndex = firstViewColors.indexOf(color);
        if (newIndex !== activeIndex) {
            if (isHover) {
                TrinityHelper.sendHoverColorChange(color.id);
            } else {
                TrinityHelper.sendClickColorChange(color.id, 'aop');
            }
        }
        setActiveIndex(newIndex);
    }

    const modifyProductUrl = useCallback((urlParam, assetId) => {
        const shopId = shopContent ? shopContent.id : 0;

        const productUrl = new URL(`${window.location.origin}${urlParam}`);
        const productParams = new URLSearchParams(productUrl.search);
        const currentUrlParams = new URLSearchParams(window.location.search);
        const dateOverride = currentUrlParams.get(Constants.SEARCH_PARAMS_DATE_OVERRIDE);
        const timeOverride = currentUrlParams.get(Constants.SEARCH_PARAMS_TIME_OVERRIDE);
        if (dateOverride && timeOverride) {
            productParams.set(Constants.SEARCH_PARAMS_DATE_OVERRIDE, dateOverride);
            productParams.set(Constants.SEARCH_PARAMS_TIME_OVERRIDE, timeOverride);
        }
        productParams.set(Constants.SEARCH_PARAMS_SHOP_ID, shopId);
        if (assetId) {
            productParams.set(Constants.SEARCH_PARAMS_ASSET_ID, assetId);
        }
        if (itemOptionId) {
            productParams.set(Constants.SEARCH_PARAMS_ITEM_OPTION_ID, itemOptionId);
        }
        if (!!filterTrackingValue) {
            productParams.set(Constants.SEARCH_PARAMS_FILTER_TRACKING_VALUE, filterTrackingValue);
        }
        if (!!rankingTrackingValue) {
            productParams.set(Constants.SEARCH_PARAMS_RANKING, rankingTrackingValue);
        }
        if(imagePresentationTypeId.current) {
            productParams.set(Constants.IMAGE_PRESENTATION_TYPE_ID, imagePresentationTypeId.current);
        }

        if (searchAttribution) {
            productParams.set(Constants.SEARCH_PARAMS_ALGOLIA_QUERY_ID, searchAttribution.queryID);
            productParams.set(Constants.SEARCH_PARAMS_ALGOLIA_POSITION, searchAttribution.position);
            if (searchAttribution.objectID) {
                productParams.set(Constants.SEARCH_PARAMS_ALGOLIA_OBJECT_ID, searchAttribution.objectID);
            }
        } else if (activeSearchEngine === 'ALGOLIA' && givenAlpi.gridItem?.algoliaObjectId) {
            // no search context (e.g. category page): still pass the objectID so the PDP can send item_clicked
            productParams.set(Constants.SEARCH_PARAMS_ALGOLIA_OBJECT_ID, givenAlpi.gridItem.algoliaObjectId);
        }
        if (algoliaFilters.length) {
            productParams.set(
                Constants.SEARCH_PARAMS_ALGOLIA_FILTER_CONTEXT,
                encodeFilterContext(algoliaFilters),
            );
        }
        productUrl.search = productParams;

        return productUrl.toString();
    }, [shopContent, itemOptionId, filterTrackingValue, rankingTrackingValue, imagePresentationTypeId, searchAttribution, algoliaFilters, activeSearchEngine, givenAlpi.gridItem?.algoliaObjectId]);

    // `object` says which part of the tile was clicked: 'pic' for the hero picture, 'link' for the
    // title/price block. Must be a useCallback rather than a plain function declaration, because the
    // click handlers below list it as a dependency — a new identity on every render would make them
    // re-create on every render, and leaving it out would let them capture a stale `currentColor`
    // after the shopper switches the tile's colour.
    const trackClick = useCallback(
        (object) => {
            WindowManager.setSpecificTabDataItem(Constants.POINT_OF_SALE_ID, pointOfSaleId);
            sendProductClick({
                pageType: pageType,
                positionInList: positionInList,
                positionOnPage: positionOnPage,
                itemNo: toLong(currentColor?.itemNumber),
                styleId: toLong(givenAlpi.gridItem?.id),
                styleNo: toLong(givenAlpi.gridItem?.masterstyleId),
                object: object,
            });
        },
        [
            pointOfSaleId,
            pageType,
            positionInList,
            positionOnPage,
            currentColor?.itemNumber,
            givenAlpi.gridItem?.id,
            givenAlpi.gridItem?.masterstyleId,
        ],
    );

    const handleClickOnAlpiInformation = useCallback(
        (e) => {
            e.preventDefault();
            trackClick('link');
            const productUrl = modifyProductUrl(currentColor.url);
            navigateToProduct(productUrl, givenAlpi.gridItem?.id, positionIndex, searchAttribution);
        },
        [
            currentColor,
            givenAlpi.gridItem.id,
            positionIndex,
            navigateToProduct,
            modifyProductUrl,
            searchAttribution,
            trackClick,
        ],
    );

    const handleClickOnHeroPicture = useCallback(
        (e, urlParam, assetId) => {
            e.preventDefault();
            const productUrl = modifyProductUrl(urlParam, assetId);
            trackClick('pic');
            navigateToProduct(productUrl, givenAlpi.gridItem?.id, positionIndex, searchAttribution);
        },
        [positionIndex, navigateToProduct, modifyProductUrl, givenAlpi.gridItem.id, searchAttribution, trackClick],
    );

    const heroPictureOnAuxClick = useCallback((e, linkTag, urlParam, assetId) => {
        // Middle click opens the product in a new tab, which is still a product click.
        if (e.button === 1) {
            trackClick('pic');
        }
        linkTag.href = modifyProductUrl(urlParam, assetId);
    }, [modifyProductUrl, trackClick]);

    const heroPictureOnContextMenu = useCallback((linkTag, urlParam, assetId) => {
        linkTag.href = modifyProductUrl(urlParam, assetId);
    }, [modifyProductUrl]);

    const placeholderClasses = `alpi__content-placeholder${isImageLoaded ? ' is-hidden' : ''}`;
    // Focus state of a product position: the product link itself has no paintable box (it is a
    // zero-size flex item inside the aspect-ratio hack and is clipped by the slider's overflow), so
    // the indicator is drawn on the tile instead, as a 1px border on an ::after overlay.
    // Overlay rather than an outline on .alpi, because the tiles sit flush against the viewport in
    // the first grid column (an outward outline is cut off there) and an inset outline would be
    // painted over by the product image. ::after is the last child, so it paints above everything.
    const focusRingClasses =
        "relative after:content-[''] after:pointer-events-none after:absolute after:inset-0 " +
        'after:rounded after:border after:border-solid after:border-transparent ' +
        'has-[[data-product-link]:focus-visible]:after:border-clr-on-bg-high';
    const clazzes = `alpi ${givenAlpi.classes ? givenAlpi.classes : ''} ${focusRingClasses}`;
    const shopId = shopContent?.id || 0;
    const channelId = shopContent?.channelId || 0;
    const productCartItem = {
        digistyleId: givenAlpi.gridItem.id,
        articleNumber: currentColor.itemNumber,
        itemId: currentColor.id,
        itemOptionId: itemOptionId,
        sizeText: currentColor.sizes?.[0]?.name || '',
        wishlistLandmarkData: {
            promotion: currentColor.promotion,
            decimalPrice: currentColor.price.value,
            shopId: shopId,
            origin: !!shopContent ? Constants.LANDMARK_PAGETYPE_AOP : Constants.LANDMARK_PAGETYPE_SRP,
        },
    };

    return (
        <div className={clazzes} ref={articleContainer} jsh-alpi={buildAlpiJshKey(givenAlpi.gridItem?.id, positionIndex)}>
            <div className="alpi__content-wrapper">
                <div className="alpi__content">
                    <div className="slider__wrapper">
                        <DefaultFallback onFallback={() => setIsImageLoaded(true)}>
                            <HeroPictureSlider
                                alpiId={givenAlpi.gridItem?.id}
                                url={currentColor.url}
                                isShopContent={shopContent !== null}
                                images={currentColor.images}
                                inView={isVisible}
                                positionIndex={positionIndex}
                                heroPictureOnClick={handleClickOnHeroPicture}
                                heroPictureOnAuxClick={heroPictureOnAuxClick}
                                heroPictureOnContextMenu={heroPictureOnContextMenu}
                                onImageLoaded={() => setIsImageLoaded(true)}
                            />
                        </DefaultFallback>
                    </div>
                    <div jsh-alpi-footer={""}>
                    {wishlistEnabled && (
                        <DefaultFallback>
                            <div className="absolute top-4 right-0 grid gap-4 items-start content-start w-[50px] h-[50px]">
                                <WishlistHeart
                                    key={`${givenAlpi.gridItem.id}-${currentColor.id}`}
                                    iconButtonVariant={'tertiary'}
                                    productCardItem={productCartItem}
                                    searchAttribution={searchAttribution}
                                    algoliaFilters={algoliaFilters}
                                />
                            </div>
                        </DefaultFallback>
                    )}

                    <DefaultFallback>
                        <ColorSwatchGrid
                            colors={firstViewColors.map(color => ({
                                name: color.name,
                                packItemsCount: color.price?.amount ?? 1,
                                segments: (color.colors && color.colors.length > 0)
                                    ? color.colors
                                    : [{colorHex: color.colorHex}],
                            }))}
                            activeIndex={activeIndex}
                            onColorSelect={(index) => {
                                const selectedColor = firstViewColors[index];
                                if (selectedColor) {
                                    onChangeItem(selectedColor, false);
                                }
                            }}
                            inView={isVisible}
                        />
                    </DefaultFallback>

                    {updatedBenefits && (
                        <DefaultFallback>
                            <Benefits benefits={updatedBenefits}  itemId={currentColor.id} />
                        </DefaultFallback>
                    )}

                    <DefaultFallback>
                        <AlpiInformation
                            title={givenAlpi.gridItem.title}
                            price={currentColor.price}
                            onAlpiInformationClick={handleClickOnAlpiInformation}
                        />
                    </DefaultFallback>
                </div>
                <div className={placeholderClasses}>
                    <Loader className="alpi__content-placeholder-loader" />
                </div>
            </div>
            </div>
        </div>
    );

    function calculateFirstViewColors(gridItem) {
        const allColors = gridItem.styles.flatMap((style) => {
            return style.colors;
        });
        return gridItem.firstViewColorIDs
            .map((id) => {
                return allColors.find((color) => {
                    return color.id === id;
                });
            })
            .filter((color) => {
                return !!color;
            });
    }
}

export default React.memo(Article);
